/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosRequestConfig,
} from 'axios';
import { actions, store } from '@core/store';

interface RefreshTokenResponse {
  error: boolean;
  msg: string;
  tokens: {
    access: string;
    refresh: string;
  };
}

interface CustomRequestConfig extends AxiosRequestConfig {
  skipAuthRefresh?: boolean;
}

interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  isRetry?: boolean;
  skipAuthRefresh?: boolean;
}

class AuthInterceptor {
  private axiosInstance: AxiosInstance;

  private isRefreshing = false;

  private failedQueue: Array<{
    resolve: (token: string) => void;
    reject: (error: Error) => void;
  }> = [];

  private tokenChannel: BroadcastChannel;

  constructor(baseURL: string) {
    this.axiosInstance = axios.create({
      baseURL,
      timeout: 30000,
    });

    // Attach a reference to this AuthInterceptor on the axiosInstance
    Object.defineProperty(this.axiosInstance, 'authInterceptor', {
      value: this,
      writable: false,
      enumerable: false,
    });

    this.tokenChannel = new BroadcastChannel('auth_token_updates');
    this.setupInterceptors();
    this.setupTokenSync();
  }

  private setupTokenSync(): void {
    this.tokenChannel.onmessage = async (event) => {
      if (event.data.type === 'TOKEN_UPDATE') {
        const { accessToken, refreshToken } = event.data;

        // Prevent redundant updates
        const currentTokens = AuthInterceptor.getTokens();
        if (currentTokens.access === accessToken && currentTokens.refresh === refreshToken) {
          return; // Tokens are already up-to-date, no need to update again
        }

        // Update store without triggering broadcast
        await actions.auth.setTokens(accessToken, refreshToken);
        // Update axios headers
        this.axiosInstance.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
        // Retry queued requests with new token
        this.processQueue(null, accessToken);
      }
    };
  }

  private processQueue(error: Error | null = null, token: string | null = null): void {
    this.failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else if (token) {
        prom.resolve(token);
      }
    });
    this.failedQueue = [];
  }

  private async refreshAuthTokens(refreshToken: string): Promise<RefreshTokenResponse> {
    try {
      const { access: accessToken } = AuthInterceptor.getTokens();
      const config: CustomRequestConfig = {
        skipAuthRefresh: true,
        headers: {
          Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
        },
      };

      const response = await this.axiosInstance.post<RefreshTokenResponse>(
        '/token/renew',
        { refreshToken },
        config
      );
      return response.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      throw error instanceof Error ? error : new Error('Unknown token refresh error');
    }
  }

  private setupInterceptors(): void {
    this.axiosInstance.interceptors.request.use(
      (config: ExtendedAxiosRequestConfig) => {
        const { access: accessToken } = AuthInterceptor.getTokens();

        if (
          accessToken &&
          AuthInterceptor.isTokenValid(accessToken) &&
          !config.headers.Authorization
        ) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        } else if (
          accessToken &&
          !AuthInterceptor.isTokenValid(accessToken) &&
          !this.isRefreshing &&
          !config.skipAuthRefresh
        ) {
          // Token is invalid but not refreshing yet - trigger refresh
          this.refreshTokens().catch(() => {
            // Silent catch - already logged in refreshTokens
          });
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as ExtendedAxiosRequestConfig;

        // Skip refresh token logic for requests that should skip auth refresh
        if (originalRequest.skipAuthRefresh) {
          return Promise.reject(error);
        }

        // Don't retry already retried requests to avoid infinite loops
        if (originalRequest.isRetry || error.response?.status !== 401) {
          return Promise.reject(error);
        }

        // If already refreshing, queue the request
        if (this.isRefreshing) {
          return new Promise((resolve, reject) => {
            this.failedQueue.push({
              resolve,
              reject: (err: Error) => reject(err),
            });
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return this.axiosInstance(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        originalRequest.isRetry = true;
        this.isRefreshing = true;
        const { refresh: refreshToken } = AuthInterceptor.getTokens();

        if (!refreshToken) {
          this.tokenChannel.postMessage({ type: 'LOGOUT' });
          this.clearTokens();
          return Promise.reject();
        }

        try {
          const response = await this.refreshAuthTokens(refreshToken);
          const { tokens } = response;

          // Update local tab
          await actions.auth.setTokens(tokens.access, tokens.refresh);
          this.axiosInstance.defaults.headers.common.Authorization = `Bearer ${tokens.access}`;

          // Notify other tabs
          this.tokenChannel.postMessage({
            type: 'TOKEN_UPDATE',
            accessToken: tokens.access,
            refreshToken: tokens.refresh,
          });

          // Retry failed requests
          this.processQueue(null, tokens.access);

          // Update auth header and send the original request
          originalRequest.headers.Authorization = `Bearer ${tokens.access}`;
          return await this.axiosInstance(originalRequest);
        } catch (refreshError) {
          const errorObj =
            refreshError instanceof Error ? refreshError : new Error('Unknown token refresh error');

          this.processQueue(errorObj, null);
          this.tokenChannel.postMessage({ type: 'LOGOUT' });
          this.clearTokens();
          throw errorObj;
        } finally {
          this.isRefreshing = false;
        }
      }
    );
  }

  private static isTokenValid(token: string): boolean {
    if (!token) return false;

    try {
      // For JWT tokens, decode and check expiration
      const parts = token.split('.');
      if (parts.length !== 3) return false;

      const payload = JSON.parse(atob(parts[1]));
      const expiryTime = (payload.expires || payload.exp) * 1000; // Convert to milliseconds

      return Date.now() < expiryTime;
    } catch (e) {
      // Silent catch - validation errors shouldn't interrupt the flow
      return false;
    }
  }

  public static getTokens(): { access: string | null; refresh: string | null } {
    try {
      const storeTokens = store.auth.tokens?.();
      return {
        access: storeTokens?.access || null,
        refresh: storeTokens?.refresh || null,
      };
    } catch (e) {
      // Fallback to localStorage or sessionStorage
      return {
        access: localStorage.getItem('access_token'),
        refresh: localStorage.getItem('refresh_token'),
      };
    }
  }

  // Added for proper token refresh handling
  public async refreshTokens(): Promise<string> {
    if (this.isRefreshing) {
      return new Promise((resolve, reject) => {
        this.failedQueue.push({ resolve, reject });
      });
    }

    this.isRefreshing = true;
    const { refresh: refreshToken } = AuthInterceptor.getTokens();

    if (!refreshToken) {
      this.isRefreshing = false;
      this.clearTokens();
      return Promise.reject();
    }

    try {
      const response = await this.refreshAuthTokens(refreshToken);
      const { tokens } = response;

      // Update tokens
      await actions.auth.setTokens(tokens.access, tokens.refresh);
      this.axiosInstance.defaults.headers.common.Authorization = `Bearer ${tokens.access}`;

      // Notify other tabs
      this.tokenChannel.postMessage({
        type: 'TOKEN_UPDATE',
        accessToken: tokens.access,
        refreshToken: tokens.refresh,
      });

      this.processQueue(null, tokens.access);
      return tokens.access;
    } catch (error) {
      const refreshError =
        error instanceof Error ? error : new Error('Unknown token refresh error');

      this.processQueue(refreshError, null);
      this.clearTokens();
      throw refreshError;
    } finally {
      this.isRefreshing = false;
    }
  }

  public queueFailedRequest(
    resolve: (token: string) => void,
    reject: (error: Error) => void
  ): void {
    this.failedQueue.push({ resolve, reject });
  }

  public async clearTokens(): Promise<void> {
    await actions.auth.removeTokens();
    actions.mobileAuth.logoutUserFromMobile();
    // Clear localStorage backups if they exist
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    // Clear authorization header
    delete this.axiosInstance.defaults.headers.common.Authorization;
  }

  public getInstance(): AxiosInstance {
    return this.axiosInstance;
  }
}

export default AuthInterceptor;
